/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import { logger } from '@touchplan/logging';
import { Container, DisplayObject } from 'pixi.js-legacy';

const settings = {
	delimiter: `/`,
};

export class LayerManager {
	/**
	 * Stores a mapping of path to FancyContainer for fast lookup of layers without having to traverse the tree.
	 */
	public layers: Map<string | number, FancyContainer> = new Map();
	/**
	 * Adds a new layer (FancyContainer) at the specified path
	 * This will recursively add any parent layers to the scene graph if they do not exist
	 * @param path The path in the scene graph to add a new FancyContainer to
	 * @param parent Optional (Deprecated) used by old canvas code to explictly define a parent object. Use a path for this purpose instead.
	 * @returns The newly created or provided container
	 */
	public addLayer(path: string, parent?: FancyContainer): FancyContainer {
		if (this.layers.get(path)) {
			throw new Error('Layer already exists: ' + path);
		}

		// This handles the old addLayer behaviour to support old code (no path traversal)
		if (!path.includes(settings.delimiter)) {
			const layer = new FancyContainer();
			layer.name = path;
			this.layers.set(path, layer);
			if (parent) {
				(parent as FancyContainer).addChild(layer);
			}
			return layer;
		}

		// Break down the path into path segments using the `/` as a delimiter
		// Then traverse the managed objects in the LayerManager to find the correct parent to add this new object to
		const segments: string[] = path.split(settings.delimiter);
		const layerName: string = segments[segments.length - 1];
		const parentPath: string = segments.slice(0, -1).join(settings.delimiter);

		let parentContainer: FancyContainer;

		if (parentPath) {
			parentContainer = this.getLayer(parentPath);
			if (!parentContainer) {
				parentContainer = this.addLayer(parentPath);
			}
		}

		if (!parentContainer) {
			throw new Error(
				`LayerManager: Scene graph traversal failed unexpectedly. Please ensure your path is not malformed (ex: parent//child): ${path}`
			);
		}

		const newContainer: FancyContainer = new FancyContainer();
		newContainer.name = layerName;

		parentContainer.addChild(newContainer);

		return newContainer;
	}
	/**
	 * Removes a layer at the specified path
	 * This will also remove all children of the layer being removed.
	 * @param path The path to the layer to remove
	 */
	public removeLayer(path: string): LayerManager {
		let layer: FancyContainer = this.layers.get(path);
		if (layer) {
			this.layers.delete(path);
			layer.removeChildren();
			if (layer.parent) {
				layer.parent.removeChild(layer);
			}
			return this;
		}

		layer = this.getLayer(path);
		if (!layer) {
			throw new Error(`LayerManager: Tried to remove a layer that does not exist: ${path}`);
		}

		layer.removeChildren();
		if (layer.parent) {
			layer.parent.removeChild(layer);
		}

		return this;
	}
	/**
	 * Returns the Layer matching name
	 */
	public getLayer(path: string): FancyContainer | null {
		if (!path.includes(`/`) || this.layers.has(path)) {
			const layer = this.layers.get(path);
			return layer ? layer : null;
		}

		const segments: string[] = path.split(settings.delimiter);
		let currentLayer: FancyContainer = this.getLayer(segments[0]);
		if (!currentLayer) {
			return null;
		}

		for (let i = 1; i < segments.length; i++) {
			const childName = segments[i];
			const nextLayer = this.findChildByName(currentLayer, childName);

			if (!nextLayer) {
				return null;
			}

			currentLayer = nextLayer;
		}

		return currentLayer;
	}
	/**
	 * Clears the layers cache (but doesn't actually destroy them in the scene graph)
	 */
	public reset() {
		this.layers.clear();
	}
	/**
	 * Invoked when the controller `AbstractApplication` is destroyed
	 * This will clear the layer cache AND destroy any managed FancyContainer
	 * @param options
	 */
	public destroy(options?): void {
		this.layers.forEach((layer: FancyContainer) => {
			layer.destroy(options);
		});

		this.layers.clear();

		this.layers = null;
	}

	/**
	 * Adds a new DisplayObject to the scene graph at the provider path
	 * Note: Like getLayer, the entire graph needs to exist before you can add objects to it.
	 * @param path
	 * @param thing
	 */
	public addChildToLayer(path: string, thing: DisplayObject): void {
		const layer: FancyContainer = this.getLayer(path);
		if (!layer) {
			throw new Error(
				`LayerManager: addChildToLayer: Tried to add DisplayObject to non-existent layer: ${path}`
			);
		}
		layer.addChild(thing);
	}
	/**
	 * Finds a child container by name within a parent container
	 * The intention here is to quickly find a specific object from a list of children
	 * @param parent The parent container to search in
	 * @param childName The name of the child to find
	 * @returns The found child container or null
	 */
	private findChildByName(parent: FancyContainer, childName: string): FancyContainer | null {
		const match: DisplayObject | undefined = parent.children.find(
			(child: FancyContainer) => child?.name === childName
		);
		return (match as FancyContainer) ?? null;
	}
}
